import { lazy, useEffect, useRef } from 'react';
import '@cfa/react-components/dist/grid.min.css';
import '@cfa/react-components/dist/styles.min.css';
import '@cfa/react-core/index.css';
import { RouterProvider } from 'react-router-dom';
import { Security } from '@okta/okta-react';
import { oktaAuth } from '../utils/auth/oktaInstance';
import { QueryClientProvider } from '@tanstack/react-query';
import './App.css';
import UserProvider from '../user/UserContext';
import { OrientationPrompt } from '../components/Global/OrientationPrompt';
import { BugsnagErrorBoundary } from '../utils/BugSnag/BugsnagErrorBoundary';
import { router } from './Routes';
import { callEvent } from '../utils/Amplitude/AmplitudeUtilities';
import { queryClient } from '../utils/api/queryClient';
import { isSessionTimeoutPromptActive } from '../utils/auth/sessionTimeoutPrompt';

const PageNotFound = lazy(() => import('../pages/PageNotFound/PageNotFound'));

const restoreOriginalUri = async (_oktaAuth: unknown, originalUri: string) => {
  window.location.replace(originalUri || '/');
};

export { oktaAuth };

const App = () => {
  const hasLoggedRenewalRef = useRef(false);
  const renewalTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const errorHandler = (error: Error) => {
      console.error('Token manager error:', error);

      // Renewal churn is expected while the session timeout prompt is up; the
      // modal owns the session decision, so don't report it as an error.
      if (isSessionTimeoutPromptActive()) {
        return;
      }

      callEvent('token manager error', error);
    };

    const renewedHandler = () => {
      if (!hasLoggedRenewalRef.current) {
        hasLoggedRenewalRef.current = true;
        callEvent('token auto-renewed', {});

        renewalTimeoutRef.current = setTimeout(() => {
          hasLoggedRenewalRef.current = false;
        }, 5000);
      }
    };

    // Token expiry is handled by the SDK's AutoRenewService. See the note in
    // AppAuthWrapper for why this app must not register its own 'expired'
    // handler.
    oktaAuth.tokenManager.on('error', errorHandler);
    oktaAuth.tokenManager.on('renewed', renewedHandler);

    return () => {
      oktaAuth.tokenManager.off('error', errorHandler);
      oktaAuth.tokenManager.off('renewed', renewedHandler);

      if (renewalTimeoutRef.current) {
        clearTimeout(renewalTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <BugsnagErrorBoundary FallbackComponent={PageNotFound as never}>
        <UserProvider>
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
          </QueryClientProvider>
          <OrientationPrompt />
        </UserProvider>
      </BugsnagErrorBoundary>
    </Security>
  );
};

export default App;
